import {CloseButton} from '@mantine/core';
import React from 'react';

import ColumnSelector from 'notebook/sample-propagation/add-samples-modal/ColumnSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {
  ALREADY_MAPPED_SUFFIX,
  NO_MATCHING_FIELDS_LABEL,
  PICK_ONE_PLACEHOLDER,
} from './columnMappingConstants';
import {useSelectTaskColumnMappingsModalStyles} from './selectTaskColumnMappingsModalStyles';
import type {ColumnSummary, TaskColumnMappingInfo} from './selectTaskColumnMappingsModalTypes';

/** Build a {@link ColumnSelector} option that's disabled and marked `(Already mapped)` when `isUsedElsewhere`. */
function mappingOption(
  name: string,
  icon: BenchlingIconName | undefined,
  isUsedElsewhere: boolean
): {name: string; icon?: BenchlingIconName; isDisabled: boolean; suffix?: string} {
  return {
    name,
    icon,
    isDisabled: isUsedElsewhere,
    suffix: isUsedElsewhere ? ALREADY_MAPPED_SUFFIX : undefined,
  };
}

/** Per-row view model: selectable task fields for the chosen source and resolved indices/flags for pickers. */
export interface MappingRowSummary {
  selectableColumns: Array<ColumnSummary>;
  destIdx: number | null;
  isSourceUnset: boolean;
}

interface MappingRowProps {
  sourceColumns: Array<TaskColumnMappingInfo>;
  rowSummary: MappingRowSummary;
  usedSourceIndices: ReadonlySet<number>;
  usedDestinationNames: ReadonlySet<string>;
  sourceColumnIndex: number | null;
  /** Position within the parent's mapping list. Used for tests to address rows by index. */
  rowIndex: number;
  showRemove: boolean;
  getSourceColumnDisplayName: (column: TaskColumnMappingInfo) => string;
  onSourceChange: (pickerIndex: number) => void;
  onDestinationChange: (pickerIndex: number) => void;
  onRemove: () => void;
}

/**
 * One row in the multi-column mapping table: source picker, arrow, destination picker, remove.
 */
export default function MappingRow({
  sourceColumns,
  rowSummary,
  usedSourceIndices,
  usedDestinationNames,
  sourceColumnIndex,
  rowIndex,
  showRemove,
  getSourceColumnDisplayName,
  onSourceChange,
  onDestinationChange,
  onRemove,
}: MappingRowProps): JSX.Element {
  const {classes} = useSelectTaskColumnMappingsModalStyles();
  const {selectableColumns, destIdx, isSourceUnset} = rowSummary;

  const sourceColumnsForPicker = sourceColumns.map((col, i) =>
    mappingOption(
      getSourceColumnDisplayName(col),
      col.columnIcon,
      i !== sourceColumnIndex && usedSourceIndices.has(i)
    )
  );

  const currentDestinationName =
    !isSourceUnset && destIdx != null && selectableColumns[destIdx] != null
      ? selectableColumns[destIdx].columnName
      : null;
  const isDestinationUnmatchable = !isSourceUnset && selectableColumns.length === 0;
  const isDestinationDisabled = isSourceUnset || isDestinationUnmatchable;
  const destinationColumnsForPicker = isDestinationUnmatchable
    ? [{name: NO_MATCHING_FIELDS_LABEL}]
    : selectableColumns.map((column) =>
        mappingOption(
          column.columnName,
          column.columnIcon,
          column.columnName !== currentDestinationName && usedDestinationNames.has(column.columnName)
        )
      );
  const destinationSelectedIndex = isDestinationUnmatchable
    ? 0
    : currentDestinationName == null
      ? null
      : selectableColumns.findIndex((s) => s.columnName === currentDestinationName);

  return (
    <div
      className={classes.mappingTableRow}
      {...getTestProps({component: 'MappingRow', element: 'root', key: `${rowIndex}`})}
      data-amp-safe={false}
    >
      <div
        className={classes.mappingCellSource}
        {...getTestProps({component: 'MappingRow', element: 'source', key: `${rowIndex}`})}
        data-amp-safe={false}
      >
        <ColumnSelector
          placeholder={PICK_ONE_PLACEHOLDER}
          columns={sourceColumnsForPicker}
          onChange={onSourceChange}
          selectedColumnIndex={sourceColumnIndex}
          disableWhenOnlyOneColumn={false}
        />
      </div>
      <span className={classes.mappingRowArrow} aria-hidden={true}>
        →
      </span>
      <div
        className={classes.mappingCellDest}
        {...getTestProps({component: 'MappingRow', element: 'destination', key: `${rowIndex}`})}
        data-amp-safe={false}
      >
        <ColumnSelector
          placeholder={PICK_ONE_PLACEHOLDER}
          columns={destinationColumnsForPicker}
          onChange={(pickerIndex: number) => {
            if (isDestinationDisabled) {
              return;
            }
            onDestinationChange(pickerIndex);
          }}
          selectedColumnIndex={destinationSelectedIndex}
          disabled={isDestinationDisabled}
          disableWhenOnlyOneColumn={false}
        />
      </div>
      {showRemove ? (
        <div className={classes.mappingCellRemove}>
          <CloseButton aria-label="Remove mapping row" onClick={onRemove} size="sm" />
        </div>
      ) : null}
    </div>
  );
}
